import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiClientService } from './api-client.service';
import { CalculatorScheduleDto } from '../Models/CalculatorScheduleDto';
import { GetPaymentScheduleOfferRequest } from '../Models/Requests/GetPaymentScheduleOfferRequest';
import { PostApplyProposalRequest } from '../Models/Requests/PostApplyProposalRequest';
import { GetCasesResponse } from '../Models/Responses/GetCasesResponse';
import { GetCaseResponse } from '../Models/Responses/GetCaseResponse';
import { HttpParams } from '@angular/common/http';
import { CaseAgreementScheduleDto } from '../Models/CaseAgreementScheduleDto';
import { GetPaymentsResponse } from '../Models/Responses/GetPaymentsResponse';
import { PostUserBindRequest } from '../Models/Requests/PostUserBindRequest';
import { PostBindUserResponse } from '../Models/Responses/PostBindUserResponse';
import { GetGenericResponse } from '../Models/Responses/GetGenericResponse';
import { GetPayByLinkStatusRequest } from '../Models/Requests/GetPayByLinkStatusRequest';
import { GetPayByLinkStatusResponse } from '../Models/Responses/GetPayByLinkStatusResponse';
import { formatDate } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class CaseService {
  private readonly apiService = inject(ApiClientService);

  public getCases(): Observable<GetCasesResponse> {
    return this.apiService.get<GetCasesResponse>('api/cases/', false);
  }

  public getCaseDetails(caseIdHash: string): Observable<GetCaseResponse> {
    return this.apiService.get<GetCaseResponse>(`api/cases/${ caseIdHash }`, false);
  }

  public assignCases(userBind: PostUserBindRequest): Observable<PostBindUserResponse> {
    const params = {
      customerNumber: userBind.customerNumber,
      pesel: userBind.pesel ? userBind.pesel : null,
      regon: userBind.regon ? userBind.regon : null,
      nip: userBind.nip ? userBind.nip : null,
    };

    return this.apiService.post<PostBindUserResponse>('api/v2/users/bind/', false, params);
  }

  public getPaymentsHistory(caseIdHash: string) : Observable<GetPaymentsResponse> {
    return this.apiService.get<GetPaymentsResponse>(`api/cases/payments/${ caseIdHash }`, false);
  }

  public getCaseAgreementSchedule(agreementIdHash: string): Observable<CaseAgreementScheduleDto> {
    return this.apiService.get<CaseAgreementScheduleDto>(`api/cases/agreements/${ agreementIdHash }`, false);
  }

  public getPaymentScheduleOffer(paymentScheduleOfferRequest: GetPaymentScheduleOfferRequest): Observable<CalculatorScheduleDto> {
    const formattedDate = formatDate(paymentScheduleOfferRequest.firstInstallmentDate, 'yyyy-MM-dd', 'en-US');
    const params = {
      caseIdHash: paymentScheduleOfferRequest.hashId,
      installmentAmount: paymentScheduleOfferRequest.installmentAmount,
      firstInstallmentDate: formattedDate,
      deliveryTypeIdHash: paymentScheduleOfferRequest.deliveryTypeIdHash,
    };

    return this.apiService.post<CalculatorScheduleDto>('api/v2/cases/agreements/calculate-schedule/', false, params);
  }

  public submitPaymentSchedule(applyProposalRequest: PostApplyProposalRequest): Observable<GetGenericResponse> {
    const formattedDate = formatDate(applyProposalRequest.firstInstallmentDate, 'yyyy-MM-dd', 'en-US');
    const params = {
      caseIdHash: applyProposalRequest.hashId,
      installmentAmount: applyProposalRequest.installmentAmount,
      firstInstallmentDate: formattedDate,
      deliveryTypeIdHash: applyProposalRequest.deliveryTypeIdHash,
      addressIdHash: applyProposalRequest.addressIdHash !== null ? applyProposalRequest.addressIdHash : null,
      phoneIdHash: applyProposalRequest.phoneIdHash !== null ? applyProposalRequest.phoneIdHash : null,
      emailIdHash: applyProposalRequest.emailIdHash !== null ? applyProposalRequest.emailIdHash : null,
    };

    return this.apiService.post<GetGenericResponse>('api/v2/cases/agreements/apply-proposal/', false, params);
  }


  public getCaseDocument(documentIdHash: string): Observable<Blob> {
    return this.apiService.get<Blob>(`api/documents/${ documentIdHash }`, false, undefined, false, 'blob');
  }

  public generatePaymentCertificate(certificateIdHash: string): Observable<GetGenericResponse> {
    return this.apiService.post<GetGenericResponse>(`api/v2/cases/order-paid-certificate/${ certificateIdHash }`, false, null);
  }

  public getPayByLinkStatus(getPayByLinkStatusRequest: GetPayByLinkStatusRequest): Observable<GetPayByLinkStatusResponse> {
    const params = new HttpParams()
      .set('serviceId', getPayByLinkStatusRequest.serviceId)
      .set('orderId', getPayByLinkStatusRequest.orderId)
      .set('hash', getPayByLinkStatusRequest.hash);

    return this.apiService.get<GetPayByLinkStatusResponse>(`api/cases/pay-by-link/status/?${ params.toString() }`, false);
  }
}
